import React, { useEffect, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { NavBarContent } from 'feature/nav/NavBar';
import { ASSESSMENT_HEADER_ACTIONS_SLOT_ID } from 'feature/nav/NavBar/NavBar.styles';
import ReportTabs from '../Tabs';
import useRetrieveData from 'global/hooks/useRetrieveData';
import useAccessRedirect from 'global/hooks/useAccessRedirect';
import { get, set, size } from 'global/lib/lodash';
import realmChange from 'global/wrappers/realmChange';
import PermissionGate from 'global/components/permission-gate';
import { useUser } from 'global/hooks/useUser';
import { REALM_ADD_LISTENERS } from '../../nav/NavRealm';
import ReportsLockIcon from '@mui/icons-material/Lock';
import Tooltip from '@mui/material/Tooltip';
import { formatSubScore } from '../AssessmentScore/SubScore/SubScoreLauncher';
import { userHasPermissions } from 'redux/actions/userLogin';
import { isActionable } from '../AssessmentScore/utils';
import sendData from '../../../redux/actions/sendData';
import { Box, Icon } from '@mui/material';
import StatusBar from '../StatusBar';
import { NavBarContent as StyledNavBarContent } from './ReportAssessment.styles';
import { HideMobileSpan } from '../Tabs/ReportTabs.styles';
import HeaderItem from 'global/components/HeaderItem';
import { useHashView } from 'feature/share/useHashView';
import ShareLinkModal from 'feature/share/ShareLinkModal';
import ShareDeletedMessage from 'feature/share/ShareDeletedMessage';
import { Button as ShareBtn } from 'global/components/Button';
import SnackAlerts from 'global/components/snack-alerts';

const AssessmentIncludes = ['realm', 'realm.realms', 'realm.parent', 'scorecards', 'scorecards.edition', 'scorecards.edition.realm', 'authors', 'editors', 'viewers', 'assignees', 'media', 'lockedBy'];

export const PScoreIncludes = 'checklist,subScores,subScores.images,subScores.realm,subScores.tradeTypes';

const include = AssessmentIncludes.join(',');

function ReportAssessment({ match, isTemplate }) {
  const { me, userBuilderRealm } = useUser();
  const { active: hashViewActive } = useHashView();
  const meId = me?.id;
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // PFM-2648: confirmation toast after a Share email is sent (modal closes on send).
  const [shareSentAlert, setShareSentAlert] = useState();
  const [state, setState] = useState({
    scores: {
      loading: true,
      data: {},
      status: null,
      update: () => 0,
    },
  });

  const assessment = useRetrieveData(
    { url: `/${isTemplate ? 'assessment-templates' : 'assessments'}/${match.params.id}` },
    {
      build: ['assessments'],
      transformData: (d) => {
        const v = d[0];
        return { ...v, availableStatus: Object.keys(v.availableStatus || []).map((k) => v.availableStatus[k]) };
      },
      params: { include },
      // PFM-2640: in a hashed share-link session a 404 here means the shared
      // assessment was deleted. Suppress the raw error alert so useAccessRedirect
      // can quietly redirect to the list with the friendly "deleted" message
      // instead of the viewer seeing a blocking dev error dialog.
      showAlert: !hashViewActive,
    }
  );

  // PFM-2565: redirect to /reports/list with an alert when this assessment is
  // inaccessible / does not exist for the current user.
  // PFM-2640: in a hashed share-link session a deleted assessment instead surfaces
  // `shareDeleted` so we can render an in-place message (anonymous viewers can't
  // see the list-page snackbar and clearing the hash would log them out).
  const { shareDeleted } = useAccessRedirect(assessment);
  const highlights = useRetrieveData(
    { url: '/report-sections' },
    {
      build: ['reportSections'],
      params: {
        include: 'superset,images,realm,origin,user,assessment,origin.superset,origin.images,progeny',
        filter: { assessment_id: match.params.id },
      },
    }
  );
  const tasks = useRetrieveData(
    { url: '/tasks' },
    {
      build: ['tasks'],
      params: {
        filter: { assessment: match.params.id },
        page: { size: 1 },
      },
    }
  );

  const scores = useRetrieveData(
    { url: '/processed-scorecard-score-values' },
    {
      build: ['processedScorecardScoreValues'],
      params: {
        filter: { assessment_id: match.params.id },
        include: PScoreIncludes,
      },
    }
  );
  const targetscores = useRetrieveData(
    { url: '/processed-scorecard-score-values' },
    {
      build: ['processedScorecardScoreValues'],
      params: {
        filter: { assessment_id: match.params.id, status: 'target' },
      },
    }
  );
  const goalscores = useRetrieveData(
    { url: '/processed-scorecard-score-values' },
    {
      build: ['processedScorecardScoreValues'],
      params: {
        filter: { assessment_id: match.params.id, status: 'goal' },
      },
    }
  );
  const filterscores = useRetrieveData(
    { url: '/processed-scorecard-score-values' },
    {
      build: ['processedScorecardScoreValues'],
      waitFor: state.scores.status,
      setLoadingEvenIfWait: true,
      params: {
        filter: { assessment_id: match.params.id, status: state.scores.status },
      },
    },
    [state.scores.status]
  );

  useEffect(() => {
    if (!scores.loading && !filterscores.loading && !targetscores.loading && !goalscores.loading) {
      const scdata = {};

      const getuid = (i) => {
        if (i.depth === 2 && !!i.scorecardScoreId) {
          // don't match to general observations
          return;
        }
        return `${i.scorecardId}_${i.systemId}_${i.supersetId}_${i.checklistId}_${i.checkpointId}`;
      };

      const isTarget = state.scores.status == 'target';

      scores.data.map((di) => {
        if (size(di.subScores)) {
          //note subscores is used instead of subScores, and this should hold true throughout
          //the rest of the site. Since we can't overwrite subScores since it doens't have a setter
          di.subscores = di.subScores.map((s) => formatSubScore(s, di));
        }

        if (get(di, 'depth') === 3) {
          set(di, 'filters.act', isActionable(di));
        }

        set(
          di,
          'compare',
          (isTarget ? targetscores.data : filterscores.data).find((d) => getuid(d) == getuid(di))
        );
        set(
          di,
          'target',
          targetscores.data.find((d) => getuid(d) == getuid(di))
        );
        set(
          di,
          'goal',
          goalscores.data.find((d) => getuid(d) == getuid(di))
        );
        set(scdata, di.scorecardId, [...get(scdata, di.scorecardId, []), di]);
      });

      setState((s) => ({
        ...s,
        scores: {
          ...s.scores,
          data: scdata,
          loading: false,
          update: async (p = {}) => {
            const updateAll = () => {
              return Promise.all([
                scores.update({ ...p, force: true }),
                filterscores.update({ ...p, force: true }),
                targetscores.update({ ...p, force: true }),
                goalscores.update({ ...p, force: true }),
              ]);
            };

            if (!p.compare) {
              assessment.update({ loading: false });
              return updateAll({});
            }

            const [st, url] = get(p, 'compare.links.self', '').split('/v1/');
            const all = await sendData(
              { url },
              {
                normalize: {
                  build: ['processedScorecardScoreValues'],
                },
                params: { include: 'parent,parent.parent,parent.parent.parent' },
              }
            );

            const uids = all.map((a) => getuid(a));

            const sc = isTarget ? targetscores : filterscores;

            const found = sc.data.find((f) => uids.includes(getuid(f)));

            let isupdating;
            const forceUpdateAll = () => {
              if (!isupdating) {
                isupdating = true;
                updateAll();
              }
            };

            if (found) {
              sc.setLoading(true);
              setTimeout(() => {
                sc.manuallySetData(
                  sc.data.map((d) => {
                    const item = all.find((a) => getuid(a) == getuid(d));
                    if (item) {
                      if (item.value === d.value) {
                        forceUpdateAll();
                      }
                      return item;
                    }
                    return d;
                  })
                );
              }, 200);
            } else {
              updateAll();
            }
          },
        },
      }));
    }
  }, [scores.loading, filterscores.loading, targetscores.loading, goalscores.loading, filterscores.data, targetscores.data, goalscores.data, state.scores.status]);

  const updateStatusFilter = (stat) => {
    setState((s) => {
      const status = stat == 'targetGoal' ? 'goal' : stat == 'active' ? null : stat;

      let loading = false;
      if (s.scores.status != status) {
        loading = true;
        filterscores.update();
        scores.update();
      }

      filterscores.setLoading(loading);

      return {
        ...s,
        scores: {
          ...s.scores,
          loading,
          status,
        },
      };
    });
  };

  const { data, loading } = assessment;

  const userIsEditor = (() => {
    if (loading || hashViewActive || !meId) {
      return false;
    }

    const { editors, authors } = data;
    let isAdmin = false;
    if (userHasPermissions(['manageAccount'])) {
      isAdmin = userBuilderRealm.id == get(data, 'vendorId') && userBuilderRealm.id == get(data, 'clientId');
    }
    const isEditor = (editors || []).find((e) => e.id == meId);
    const isAuthor = (authors || []).find((e) => e.id == meId);
    return isAdmin || isEditor || isAuthor;
  })();

  const userCanEdit = (() => {
    if (loading || hashViewActive || !meId) {
      return false;
    }
    if (get(data, 'locked') && !get(data, 'lockedBy', []).find((u) => u.id == meId)) {
      return false;
    }
    return userIsEditor;
  })();

  const status = get(assessment, 'data.status', 'draft');

  const canShowScores = (() => {
    if (userIsEditor) {
      return true;
    }
    if (status === 'review') {
      if (hashViewActive || !meId) return true;
      return get(assessment, 'data.viewers', []).find((v) => v.id == meId) || get(assessment, 'data.assignees', []).find((v) => v.id == meId);
    } else if (status === 'published') {
      return true;
    } else {
      return false;
    }
  })();

  const userCanModify = userCanEdit && status !== 'published';
  const canEditStatus = isTemplate ? !hashViewActive && userHasPermissions(['manageAccount']) : userCanModify;
  const statusTitle =
    {
      draft: 'Draft',
      review: 'Review',
      published: 'Final',
    }[status] || status;

  useEffect(() => {
    //need to update the assessment if assessments were added from the realm picker
    window[REALM_ADD_LISTENERS].assessment = async () => {
      await assessment.update();
    };

    return () => {
      delete window[REALM_ADD_LISTENERS].assessment;
    };
  }, []);

  const navBarContent = (
    <StyledNavBarContent gap={2}>
      <Box
        sx={{
          flexDirection: 'row',
          alignItems: 'top',
          gap: 5,
          display: 'flex',
        }}
      >
        <HeaderItem label="Location" value={get(data, 'realm.name')} />
        <HeaderItem label="Author" value={get(data, 'authors.0.name', 'N/A')} showInMobile={false} />
        <HeaderItem
          showInMobile={false}
          label="Scorecards"
          value={get(data, 'scorecards', [])
            .map((s) => s.name)
            .join(', ')}
        />
        {isTemplate || userCanModify ? (
          <HeaderItem content={<StatusBar assessment={assessment} scores={state.scores} userCanEdit={userCanEdit} canEditStatus={canEditStatus} isTemplate={isTemplate} />} />
        ) : (
          <HeaderItem label="Status" value={statusTitle} />
        )}
      </Box>
    </StyledNavBarContent>
  );

  const shareActions =
    !hashViewActive && !isTemplate ? (
      <ShareBtn data-testid="assessment-share-btn" onClick={() => setShareModalOpen(true)}>
        {/* Icon shows on both desktop and mobile; the text label is desktop-only
            (HideMobileSpan). Margin-right only applies on desktop where text follows. */}
        <Icon sx={{ fontSize: '1.1rem', verticalAlign: 'middle', mr: { xs: 0, lg: 0.5 } }}>ios_share</Icon>
        <HideMobileSpan>Share</HideMobileSpan>
      </ShareBtn>
    ) : null;

  // PFM-2640: the redeemed share link resolved but the shared assessment is gone.
  // Render an in-place "deleted" message (no redirect, hash session left intact so
  // the App.js anonymous-logout guard never fires). Applies to anonymous viewers
  // and logged-in viewers without normal access (both are in a hashed view).
  if (shareDeleted) {
    return <ShareDeletedMessage />;
  }

  return (
    <>
      {data && (
        <NavBarContent
          space={true}
          actionsSlotId={ASSESSMENT_HEADER_ACTIONS_SLOT_ID}
          pageTitle={
            get(data, 'locked') ? (
              <>
                <Tooltip title={get(data, 'locked') ? 'Locked by: ' + get(data, 'lockedBy.0.name') : ''} placement="left">
                  <ReportsLockIcon style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                </Tooltip>
                {data.name}
              </>
            ) : (
              data.name
            )
          }
          link="reports/list"
        >
          {navBarContent}
        </NavBarContent>
      )}
      <Switch>
        <Route
          exact
          path="/reports/assessment/:id"
          render={(props) => (
            <ReportTabs
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              userCanModify={userCanModify}
              canShowScores={canShowScores}
              updateStatusFilter={updateStatusFilter}
              shareActions={shareActions}
              currentTabValue="scores"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment-template/:id"
          render={(props) => (
            <ReportTabs
              isTemplate
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              userCanModify={userCanModify}
              canShowScores={canShowScores}
              updateStatusFilter={updateStatusFilter}
              shareActions={shareActions}
              currentTabValue="scores"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/info"
          render={(props) => (
            <ReportTabs
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              canShowScores={canShowScores}
              shareActions={shareActions}
              currentTabValue="info"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment-template/:id/info"
          render={(props) => (
            <ReportTabs
              isTemplate
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              canShowScores={canShowScores}
              shareActions={shareActions}
              currentTabValue="info"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/scores"
          render={(props) => (
            <ReportTabs
              isTemplate={isTemplate}
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              userCanModify={userCanModify}
              canShowScores={canShowScores}
              updateStatusFilter={updateStatusFilter}
              shareActions={shareActions}
              currentTabValue="scores"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment-template/:id/scores"
          render={(props) => (
            <ReportTabs
              isTemplate={isTemplate}
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              userCanModify={userCanModify}
              canShowScores={canShowScores}
              updateStatusFilter={updateStatusFilter}
              shareActions={shareActions}
              currentTabValue="scores"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/highlights"
          render={(props) => (
            <ReportTabs
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              canShowScores={canShowScores}
              shareActions={shareActions}
              currentTabValue="highlights"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment-template/:id/highlights"
          render={(props) => (
            <ReportTabs
              isTemplate
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              canShowScores={canShowScores}
              shareActions={shareActions}
              currentTabValue="highlights"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/photos"
          render={(props) => (
            <ReportTabs
              isTemplate={isTemplate}
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights}
              tasks={tasks}
              userCanEdit={userCanEdit}
              canShowScores={canShowScores}
              assessmentId={match.params.id}
              shareActions={shareActions}
              currentTabValue="photos"
            />
          )}
        />
      </Switch>
      {shareModalOpen && <ShareLinkModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} onSent={({ message }) => setShareSentAlert({ message })} scope={{ kind: 'assessment', id: match.params.id }} title={get(data, 'name', 'assessment')} />}
      <SnackAlerts alerts={shareSentAlert} setAlerts={setShareSentAlert} />
    </>
  );
}

export default PermissionGate(realmChange(ReportAssessment));
